/* Use the theme's modal focus lifecycle and product-form/cart confirmation.
 * Only product options and the selected color's gallery are fetched on demand. */
(() => {
  const ThemeModal = customElements.get('modal-dialog');
  if (!ThemeModal || customElements.get('ayaq-premium-quick-add')) return;
  let formScript;
  function loadForm(src) {
    if (customElements.get('product-form')) return Promise.resolve();
    if (!formScript) {
      formScript = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = () => {
          script.remove();
          formScript = null;
          reject(new Error('Product form unavailable'));
        };
        document.head.append(script);
      });
    }
    return formScript;
  }

  class PremiumQuickAdd extends ThemeModal {
    connectedCallback() {
      // Remain inside the section so Theme Editor removal also removes the modal.
      if (this.events) return;
      this.events = new window.AbortController();
      this.content = this.querySelector('[data-quick-content]');
      this.status = this.querySelector('[role="status"]');
      document.addEventListener(
        'ayaq:cart-modal-open',
        () => {
          if (this.hasAttribute('open')) this.hide(true);
        },
        { signal: this.events.signal },
      );
      this.desktop = window.matchMedia(
        '(min-width: 990px) and (hover: hover) and (pointer: fine)',
      );
      this.desktop.addEventListener(
        'change',
        () => {
          if (!this.desktop.matches && this.hasAttribute('open')) this.hide();
        },
        { signal: this.events.signal },
      );
      this.addEventListener(
        'click',
        (event) => {
          const option = event.target.closest('[data-quick-variant-url]');
          if (!option || option.getAttribute('aria-pressed') === 'true') return;
          this.load(option.dataset.quickVariantUrl, option.textContent.trim());
        },
        { signal: this.events.signal },
      );
    }

    disconnectedCallback() {
      if (this.hasAttribute('open')) this.hide();
      this.request?.abort();
      this.events?.abort();
      this.events = null;
    }

    show(opener) {
      if (!this.desktop.matches) return;
      opener.closest('ayaq-premium-card')?.setAttribute('data-quick-open', '');
      super.show(opener);
      this.load(opener.href, opener.textContent.trim());
    }

    hide(preventFocus = false) {
      this.request?.abort();
      const card = this.openedBy?.closest('ayaq-premium-card');
      const cart = document.querySelector('cart-notification, cart-drawer');
      cart?.setActiveElement(this.openedBy);
      if (preventFocus || !this.openedBy?.isConnected) this.openedBy = null;
      super.hide();
      card?.removeAttribute('data-quick-open');
      this.content.replaceChildren();
      this.status.hidden = true;
    }

    async load(href, focusLabel) {
      this.request?.abort();
      const request = new window.AbortController();
      this.request = request;
      this.setAttribute('aria-busy', 'true');
      this.content.inert = true;
      this.status.hidden = true;
      try {
        const url = new window.URL(href, window.location.origin);
        url.searchParams.set('section_id', 'ayaq-premium-quick-add');
        const [response] = await Promise.all([
          fetch(url, { signal: request.signal }),
          loadForm(this.dataset.formScript),
        ]);
        if (!response.ok) throw new Error('Product options unavailable');
        const document = new window.DOMParser().parseFromString(
          await response.text(),
          'text/html',
        );
        const product = document.querySelector('[data-premium-quick-content]');
        if (!product || request.signal.aborted) {
          throw new Error('No eligible variant');
        }
        this.content.replaceChildren(product);
        this.content.inert = false;
        const focus = Array.from(
          this.querySelectorAll('[data-quick-variant-url]'),
        ).find(
          (button) =>
            button.textContent.trim() === focusLabel &&
            button.getAttribute('aria-pressed') === 'true',
        );
        window.trapFocus(this, focus || this.querySelector('[role="dialog"]'));
      } catch {
        if (request.signal.aborted) return;
        this.status.textContent = this.dataset.error;
        const link = document.createElement('a');
        link.href = href;
        link.textContent =
          this.openedBy?.closest('ayaq-premium-card')?.querySelector('h2')
            ?.textContent ||
          this.openedBy?.textContent ||
          '';
        this.status.append(' ', link);
        this.status.hidden = false;
        window.trapFocus(this, link);
      } finally {
        if (this.request === request) {
          this.removeAttribute('aria-busy');
          this.content.inert = false;
        }
      }
    }
  }
  customElements.define('ayaq-premium-quick-add', PremiumQuickAdd);
})();
